import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import {
  format,
  addDays,
  addWeeks,
  addHours,
  roundToNearestMinutes,
  startOfWeek,
  endOfWeek,
  isSameDay,
} from "date-fns";
import api from "../utils/apiConfig";

import logger from '../utils/logger';

// Event type definitions
export const EVENT_TYPES = {
  CLIENT: {
    label: "Client Meeting",
    color: "#029831",
    borderColor: "#018529",
  },
  PROSPECT: {
    label: "Prospect Meeting",
    color: "#FF9800",
    borderColor: "#F57C00",
  },
  INTERNAL: {
    label: "Internal Meeting",
    color: "#3B82F6",
    borderColor: "#2563EB",
  },
  EXTERNAL: {
    label: "External Meeting",
    color: "#9C27B0",
    borderColor: "#7B1FA2",
  },
  WORKSHOP: {
    label: "Workshop",
    color: "#f4511e",
    borderColor: "#f4511e",
  },
  TASK: {
    label: "Task",
    color: "#FFC107",
    borderColor: "#FFA000",
  },
  TRAINING: {
    label: "Training",
    color: "#8B5CF6",
    borderColor: "#7C3AED",
  },
  PERSONAL: {
    label: "Personal",
    color: "#EC4899",
    borderColor: "#DB2777",
  },
  OTHER: {
    label: "Other",
    color: "#6B7280",
    borderColor: "#4B5563",
  },
};

// Meeting content for random generation
const MEETING_CONTENT = {
  clientNames: [
    "Smith Family",
    "Johnson Trust",
    "Williams Group",
    "Brown Estate",
    "Davis Holdings",
    "Miller Investments",
    "Wilson Legacy",
    "Moore Family",
    "Taylor Group",
    "Anderson Trust",
    "Thomas Family",
    "Jackson Wealth",
    "White Estate",
    "Harris Investments",
    "Martin Family",
    "Thompson Trust",
    "Garcia Holdings",
    "Martinez Family",
    "Robinson Group",
    "Clark Estate",
  ],
  prospectNames: [
    "Innovative Solutions LLC",
    "Global Ventures Inc",
    "Future Tech Corp",
    "Strategic Partners Group",
    "Summit Enterprises",
    "Pinnacle Industries",
    "Horizon Investments",
    "Elite Consulting Group",
    "Premier Solutions",
    "Nexus Corporation",
    "Apex Holdings",
    "Catalyst Ventures",
    "Dynamic Systems Inc",
    "Quantum Enterprises",
    "Synergy Group",
    "Precision Partners",
    "Elevate Solutions",
    "Vertex Industries",
  ],
  externalPartners: [
    "BlackRock",
    "Vanguard",
    "Fidelity",
    "Charles Schwab",
    "JP Morgan",
    "Goldman Sachs",
    "Morgan Stanley",
    "State Street",
    "BNY Mellon",
    "Northern Trust",
    "T. Rowe Price",
    "Invesco",
    "Franklin Templeton",
  ],
  clientTopics: [
    "Portfolio Review",
    "Wealth Planning",
    "Estate Planning",
    "Retirement Strategy",
    "Investment Review",
    "Tax Planning",
    "Legacy Planning",
    "Financial Goals",
    "Asset Allocation",
    "Risk Assessment",
    "Insurance Review",
    "Succession Planning",
  ],
  prospectTopics: [
    "Initial Consultation",
    "Investment Strategy",
    "Wealth Management",
    "Portfolio Analysis",
    "Financial Planning",
    "Service Overview",
    "Strategy Presentation",
    "Needs Assessment",
    "Solution Proposal",
  ],
  workshopTopics: [
    "Investment Strategies",
    "Market Analysis",
    "Risk Management",
    "Tax Efficiency",
    "Estate Planning",
    "Retirement Planning",
    "Client Communication",
    "Technology Training",
    "Compliance Update",
    "Product Training",
    "Best Practices",
    "Industry Trends",
  ],
  internalTopics: [
    "Team Strategy",
    "Department Sync",
    "Performance Review",
    "Project Planning",
    "Process Improvement",
    "Training Session",
    "Goal Setting",
    "Resource Planning",
    "Operations Review",
  ],
};

// Helper function to get random item from array
const getRandomItem = (array) =>
  array[Math.floor(Math.random() * array.length)];

// Helper function to generate random meeting title
const generateMeetingTitle = (type) => {
  logger.info("Generating meeting title:", { type, hasGeneratedFirstMeeting });

  // Special case for household ID 253 - always make the first meeting "Annual Review - Smith Household"
  if (type === "CLIENT" && !hasGeneratedFirstMeeting) {
    logger.info("Generating first meeting title for Smith Household");
    hasGeneratedFirstMeeting = true;
    return "Annual Review - Smith Household";
  }

  logger.info("Generating random meeting title");
  switch (type) {
    case "CLIENT":
      return `${getRandomItem(MEETING_CONTENT.clientTopics)} - ${getRandomItem(MEETING_CONTENT.clientNames)}`;
    case "PROSPECT":
      return `${getRandomItem(MEETING_CONTENT.prospectTopics)} - ${getRandomItem(MEETING_CONTENT.prospectNames)}`;
    case "EXTERNAL":
      return `Partner Meeting - ${getRandomItem(MEETING_CONTENT.externalPartners)}`;
    case "WORKSHOP":
      return `${getRandomItem(MEETING_CONTENT.workshopTopics)} Workshop`;
    case "INTERNAL":
      return `${getRandomItem(MEETING_CONTENT.internalTopics)} Meeting`;
    default:
      return "Meeting";
  }
};

// Track if we've generated the first meeting - reset this when events are reinitialized
let hasGeneratedFirstMeeting = false;

// Event status definitions
export const EVENT_STATUS = {
  SCHEDULED: "scheduled",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  RESCHEDULED: "rescheduled",
};

// Weekly schedule template
export const WEEKLY_SCHEDULE = {
  1: [
    // Monday
    {
      type: "WORKSHOP",
      startTime: "14:00",
      duration: 120,
    },
    {
      type: "INTERNAL",
      startTime: "16:30",
      duration: 60,
    },
  ],
  2: [
    // Tuesday
    {
      type: "PROSPECT",
      startTime: "14:00",
      duration: 60,
    },
    {
      type: "INTERNAL",
      startTime: "15:30",
      duration: 30,
    },
  ],
  3: [
    // Wednesday
    {
      type: "WORKSHOP",
      startTime: "11:30",
      duration: 120,
    },
    {
      type: "EXTERNAL",
      startTime: "14:00",
      duration: 120,
    },
    {
      type: "PROSPECT",
      startTime: "16:30",
      duration: 60,
    },
  ],
  4: [
    // Thursday
    {
      type: "EXTERNAL",
      startTime: "13:30",
      duration: 90,
    },
    {
      type: "INTERNAL",
      startTime: "15:30",
      duration: 60,
    },
  ],
  5: [
    // Friday
    {
      type: "WORKSHOP",
      startTime: "13:30",
      duration: 120,
    },
    {
      type: "INTERNAL",
      startTime: "16:00",
      duration: 60,
    },
  ],
};

// Special weekend events that occur periodically
export const SPECIAL_WEEKEND_EVENTS = [
  {
    title: "Annual Investment Conference",
    type: "EXTERNAL",
    duration: 480,
    startTime: "09:00",
    color: EVENT_TYPES.EXTERNAL.color,
    dayOffset: 6, // Saturday
  },
  {
    title: "Quarterly Market Outlook Presentation",
    type: "EXTERNAL",
    duration: 180,
    startTime: "13:00",
    color: EVENT_TYPES.EXTERNAL.color,
    dayOffset: 6, // Saturday
  },
  {
    title: "Regional Advisory Summit",
    type: "EXTERNAL",
    duration: 360,
    startTime: "10:00",
    color: EVENT_TYPES.EXTERNAL.color,
    dayOffset: 0, // Sunday
  },
];

// Helper function to fetch household data
const fetchHouseholdData = async (householdId) => {
  try {
    const response = await api.get(
      `/advisor-portal/client-database/household-profile/${householdId}`,
    );
    const data = response.data;
    const lastName = data.members?.[0]?.lastName || "Smith";
    return lastName;
  } catch (error) {
    logger.error(`Error fetching household data for ID ${householdId}:`, error);
    return householdId === 253 ? "Smith" : "Thompson";
  }
};

export const generateScheduledEvents = async (startDate = new Date()) => {
  logger.info(
    "Starting generateScheduledEvents, hasGeneratedFirstMeeting:",
    hasGeneratedFirstMeeting,
  );

  const events = [];
  const today = startDate;

  // Reset the flag at the start of generation
  hasGeneratedFirstMeeting = false;

  // 1. Generate first demo event - Annual Review for household 253
  const firstLastName = await fetchHouseholdData(253);
  events.push({
    id: "demo-annual-review",
    title: `Annual Review - ${firstLastName} Household`,
    start: (() => {
      const start = new Date(today);
      start.setHours(8, 0, 0, 0);
      return start;
    })(),
    end: (() => {
      const end = new Date(today);
      end.setHours(9, 30, 0, 0);
      return end;
    })(),
    type: "CLIENT",
    backgroundColor: "#029831",
    borderColor: "#029831",
    textColor: "#FFFFFF",
    extendedProps: {
      type: "CLIENT",
      attendees: [],
      virtualMeetingLink: "",
      status: EVENT_STATUS.SCHEDULED,
      location: "Virtual",
      description: "Annual review meeting",
    },
  });

  // 2. Generate second demo event - Initial Financial Consultation for household 283
  const secondLastName = await fetchHouseholdData(283);
  events.push({
    id: "demo-new-client",
    title: `Initial Financial Consultation - ${secondLastName} Household`,
    start: (() => {
      const start = new Date(today);
      start.setHours(9, 30, 0, 0);
      return start;
    })(),
    end: (() => {
      const end = new Date(today);
      end.setHours(11, 0, 0, 0);
      return end;
    })(),
    type: "CLIENT",
    backgroundColor: "#029831",
    borderColor: "#029831",
    textColor: "#FFFFFF",
    extendedProps: {
      type: "CLIENT",
      attendees: [],
      virtualMeetingLink: "",
      status: EVENT_STATUS.SCHEDULED,
      location: "Virtual",
      description: "Initial financial consultation",
    },
  });

  // Set flag to true after generating demo events
  hasGeneratedFirstMeeting = true;

  // 3. Generate remaining events from weekly schedule
  const currentDate = new Date(today);
  currentDate.setMonth(currentDate.getMonth() - 1);

  // Generate events for 2 months (1 month past + 1 month future)
  for (let week = 0; week < 8; week++) {
    for (let day = 1; day <= 5; day++) {
      const daySchedule = WEEKLY_SCHEDULE[day] || [];
      daySchedule.forEach((template) => {
        // Skip if this is today's 8:00 or 9:30 CLIENT slot (we already have demo events there)
        const isToday = isSameDay(currentDate, today);
        const [hours, minutes] = template.startTime.split(":").map(Number);
        if (
          isToday &&
          template.type === "CLIENT" &&
          ((hours === 8 && minutes === 0) || (hours === 9 && minutes === 30))
        ) {
          return;
        }

        const eventDate = new Date(currentDate);
        eventDate.setDate(currentDate.getDate() + (day - currentDate.getDay()));
        eventDate.setHours(hours, minutes, 0, 0);

        const endDate = new Date(eventDate);
        endDate.setMinutes(endDate.getMinutes() + template.duration);

        const title = generateMeetingTitle(template.type);

        const event = {
          id: `${eventDate.toISOString()}-${template.type}`,
          title: title,
          start: eventDate,
          end: endDate,
          allDay: false,
          type: template.type,
          backgroundColor: EVENT_TYPES[template.type].color,
          borderColor: EVENT_TYPES[template.type].color,
          extendedProps: {
            type: template.type,
            status: EVENT_STATUS.SCHEDULED,
            attendees: [],
            virtualMeetingLink: "",
            location: "",
            description: "",
          },
        };
        events.push(event);
      });
    }
    currentDate.setDate(currentDate.getDate() + 7); // Move to next week
  }

  logger.info("All events generated:", events);
  return events;
};

// Generate mock tasks
export const generateMockTasks = (startDate = new Date()) => {
  // Use the provided startDate or today
  const today = startDate || new Date();
  today.setHours(0, 0, 0, 0);

  const tasks = [
    {
      title: "Review Client Portfolio - Q2 Performance",
      type: "TASK",
      dueDate: new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000),
      status: EVENT_STATUS.PENDING,
    },
    {
      title: "Prepare Investment Report - Johnson Family",
      type: "TASK",
      dueDate: new Date(today.getTime() + 4 * 24 * 60 * 60 * 1000),
      status: EVENT_STATUS.PENDING,
    },
    {
      title: "Update Financial Models - Q3 Projections",
      type: "TASK",
      dueDate: new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000),
      status: EVENT_STATUS.PENDING,
    },
    {
      title: "Draft Client Proposal - New Opportunities",
      type: "TASK",
      dueDate: new Date(today.getTime() + 5 * 24 * 60 * 60 * 1000),
      status: EVENT_STATUS.PENDING,
    },
    {
      title: "Research Market Trends - Emerging Markets",
      type: "TASK",
      dueDate: new Date(today.getTime() + 1 * 24 * 60 * 60 * 1000),
      status: EVENT_STATUS.PENDING,
    },
  ];

  return tasks.map((task) => {
    // Set the due date to end of day (23:59:59)
    const dueDate = new Date(task.dueDate);
    dueDate.setHours(23, 59, 59, 999);

    return {
      id: `task-${dueDate.toISOString()}-${task.title}`,
      title: task.title,
      start: dueDate.toISOString(),
      end: dueDate.toISOString(),
      allDay: true,
      backgroundColor: EVENT_TYPES.TASK.color,
      borderColor: EVENT_TYPES.TASK.borderColor,
      type: task.type,
      display: "background",
      extendedProps: {
        type: "TASK",
        status: task.status,
        dueDate: dueDate.toISOString(),
        attendees: [],
      },
    };
  });
};

const EventContext = createContext();

// Track if we've already initialized events
let eventsInitialized = false;

export const EventProvider = ({ children }) => {
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);

  const initializeEvents = useCallback(async () => {
    // Only initialize events if we have a valid token and haven't initialized yet
    const token = localStorage.getItem("token");
    if (!token || eventsInitialized) {
      logger.info("Skipping event initialization - no token or already initialized");
      return;
    }

    logger.info("Initializing events for the first time");
    eventsInitialized = true;
    const scheduledEvents = await generateScheduledEvents();
    logger.info("Generated scheduled events:", scheduledEvents);
    const mockTasks = generateMockTasks();
    setEvents(scheduledEvents);
    setTasks(mockTasks);
  }, []);

  // Reset initialization flag when token changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "token" && !e.newValue) {
        eventsInitialized = false;
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    // Only initialize events if we have a valid token
    const token = localStorage.getItem("token");
    if (token) {
      initializeEvents();
    }
  }, [initializeEvents]);

  // Clean event title (remove #-numbers)
  const cleanEventTitle = (title) => {
    return title.replace(/\s*-\s*#\d+$/, "");
  };

  // Get event status
  const getEventStatus = (event) => {
    if (event.type === "TASK") {
      const now = new Date();
      const end = new Date(event.end);
      return now > end ? EVENT_STATUS.OVERDUE : event.extendedProps.status;
    }
    return event.extendedProps.status;
  };

  // Add or update event with proper handling of attendees and virtual meeting
  const updateEvent = useCallback((event) => {
    const processedEvent = {
      ...event,
      extendedProps: {
        ...event.extendedProps,
        attendees: event.extendedProps?.attendees || [],
        virtualMeetingLink: event.extendedProps?.virtualMeetingLink || null,
        status: event.extendedProps?.status || EVENT_STATUS.PENDING,
      },
    };

    setEvents((prevEvents) => {
      const index = prevEvents.findIndex((e) => e.id === event.id);
      if (index >= 0) {
        const updatedEvents = [...prevEvents];
        updatedEvents[index] = processedEvent;
        return updatedEvents;
      }
      return [...prevEvents, processedEvent];
    });
  }, []);

  // Add or update task with proper handling of attendees and virtual meeting
  const updateTask = useCallback((task) => {
    const processedTask = {
      ...task,
      extendedProps: {
        ...task.extendedProps,
        attendees: task.extendedProps?.attendees || [],
        virtualMeetingLink: task.extendedProps?.virtualMeetingLink || null,
        status: task.extendedProps?.status || EVENT_STATUS.PENDING,
      },
    };

    setTasks((prevTasks) => {
      const index = prevTasks.findIndex((t) => t.id === task.id);
      if (index >= 0) {
        const updatedTasks = [...prevTasks];
        updatedTasks[index] = processedTask;
        return updatedTasks;
      }
      return [...prevTasks, processedTask];
    });
  }, []);

  // Add attendee to event or task
  const addAttendee = useCallback(
    (eventId, attendee) => {
      const event =
        events.find((e) => e.id === eventId) ||
        tasks.find((t) => t.id === eventId);
      if (!event) return;

      const updatedAttendees = [
        ...(event.extendedProps.attendees || []),
        {
          ...attendee,
          response: "pending",
        },
      ];

      if (event.type === "TASK") {
        updateTask({
          ...event,
          extendedProps: {
            ...event.extendedProps,
            attendees: updatedAttendees,
          },
        });
      } else {
        updateEvent({
          ...event,
          extendedProps: {
            ...event.extendedProps,
            attendees: updatedAttendees,
          },
        });
      }
    },
    [events, tasks, updateEvent, updateTask],
  );

  // Update virtual meeting link
  const updateVirtualMeeting = useCallback(
    (eventId, link) => {
      const event =
        events.find((e) => e.id === eventId) ||
        tasks.find((t) => t.id === eventId);
      if (!event) return;

      if (event.type === "TASK") {
        updateTask({
          ...event,
          extendedProps: {
            ...event.extendedProps,
            virtualMeetingLink: link,
          },
        });
      } else {
        updateEvent({
          ...event,
          extendedProps: {
            ...event.extendedProps,
            virtualMeetingLink: link,
          },
        });
      }
    },
    [events, tasks, updateEvent, updateTask],
  );

  const value = {
    events,
    tasks,
    initializeEvents,
    updateEvent,
    updateTask,
    cleanEventTitle,
    getEventStatus,
    addAttendee,
    updateVirtualMeeting,
  };

  return (
    <EventContext.Provider value={value}>{children}</EventContext.Provider>
  );
};

export const useEvents = () => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error("useEvents must be used within an EventProvider");
  }
  return context;
};

export default EventContext;
